import { useState, useEffect, useMemo, useRef } from 'react'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import NotaCreditoPreviewPDF from '../../../../../table/typesExportPDF/NotaCreditoPreviewPDF'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import { calcAmountsForRow } from '../../utils/dteAmounts'

export default function CreditNotePreviewView({ currentBusiness, baseDoc, onBack, onConfirm }) {
  const api = useMemo(() => new FacturaAPI(), [])
  const [loadingDetails, setLoadingDetails] = useState(true)
  console.log(baseDoc)
  // Estados para datos enriquecidos (dirección, giro, etc.)
  const [issuerDetails, setIssuerDetails] = useState(null)
  const [receiverDetails, setReceiverDetails] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoadingDetails(true)

    const fetchAll = async () => {
      try {
        // Helper interno para este scope
        const cleanRut = rut => {
          if (!rut) return null
          const clean = rut.replace(/\./g, '').replace(/-/g, '').trim()
          if (clean.length < 2) return clean
          return `${clean.slice(0, -1)}-${clean.slice(-1)}`
        }

        // 1. Emisor (nuestra empresa)
        const rutIssuer = cleanRut(currentBusiness?.rut || baseDoc?.sender_tax_id)
        let issuerData = null
        if (rutIssuer) {
          const resI = await api.getSearchEntityRut(rutIssuer)
          issuerData = resI?.data || null
        }

        // 2. Receptor (el cliente de la factura original)
        const rutReceiver = cleanRut(baseDoc?.receiver_tax_id)
        let receiverData = null
        if (rutReceiver) {
          const resR = await api.getSearchEntityRut(rutReceiver)
          receiverData = resR?.data || null
        }

        if (!cancelled) {
          setIssuerDetails(issuerData)
          setReceiverDetails(receiverData)
        }
      } catch (err) {
        console.error('Error fetching entity details for NC preview:', err)
      } finally {
        if (!cancelled) setLoadingDetails(false)
      }
    }

    fetchAll()
    return () => {
      cancelled = true
    }
  }, [baseDoc, currentBusiness, api])

  // Helper para sacar la dirección más "principal" o la primera
  const getAddressStr = entityData => {
    const arr = entityData?.addresses || []
    if (!arr.length) return ''
    // Podrías buscar una 'is_main' si existiera, o tomar la primera
    const addrObj = arr[0]
    return addrObj?.address || ''
  }
  const getDistrictStr = entityData => {
    const arr = entityData?.addresses || []
    return arr[0]?.district?.name || ''
  }
  const getCityStr = entityData => {
    const arr = entityData?.addresses || []
    return arr[0]?.district?.city?.name || ''
  }
  const getActivityStr = entityData => {
    const acts = entityData?.activities || []
    return acts[0]?.name || '' // Tomamos la primera actividad
  }

  // Helper para formatear RUT según requerimientos: sin puntos y con guión (ej: 12345678-9)
  const formatRutNC = rut => {
    if (!rut) return '-'
    const clean = rut.replace(/\./g, '').replace(/-/g, '').trim()
    if (clean.length < 2) return clean
    const body = clean.slice(0, -1)
    const dv = clean.slice(-1)
    return `${body}-${dv}`
  }

  // El resto del módulo trata details[].unit_price como monto NETO (sin IVA),
  // pero baseDoc.amount_with_iva viene con IVA incluido -> hay que descomponerlo
  // acá o el preview/PDF de la NC vuelve a aplicar el 19% sobre un monto que ya lo tenía.
  const baseAmounts = calcAmountsForRow(baseDoc)
  const baseUnitPrice = baseAmounts.kind === 'exento' ? baseAmounts.exento : baseAmounts.neto

  // Construcción del objeto "document" para la previsualización
  const previewDocument = {
    date_issued: new Date().toISOString().slice(0, 10),
    // Código 61 = Nota de Crédito Electrónica
    dte_type: { code: '61', description: 'Nota de Crédito Electrónica' },
    document_issuer: {
      rut: currentBusiness?.rut || baseDoc?.sender_tax_id,
      business_name: currentBusiness?.business_name || baseDoc?.sender_name,
      email: currentBusiness?.email || issuerDetails?.email,
      // Si tenemos datos del endpoint, usamos esos. Si no, fallbacks.
      business_activity: getActivityStr(issuerDetails) || currentBusiness?.business_activity || '',
      address: getAddressStr(issuerDetails) || currentBusiness?.address || '',
      district: getDistrictStr(issuerDetails) || currentBusiness?.district || '',
      city: getCityStr(issuerDetails) || currentBusiness?.city || '',
      phone_number: currentBusiness?.phone || issuerDetails?.addresses?.[0]?.phone || ''
    },
    document_receiver: {
      rut: formatRutNC(baseDoc?.receiver_tax_id),
      business_name: baseDoc?.receiver_name,
      // Intentamos sacar giro/dirección del endpoint del receptor
      business_activity:
        getActivityStr(receiverDetails) || baseDoc?.receiver_business_activity || '',
      address: getAddressStr(receiverDetails) || baseDoc?.receiver_address || '',
      district: getDistrictStr(receiverDetails) || baseDoc?.receiver_district || '',
      city: getCityStr(receiverDetails) || baseDoc?.receiver_city || ''
    },
    // Referencia al documento de origen
    references: [
      {
        // 33 = Factura, 34 = Exenta... lo sacamos de dte_type_code
        dte_type_code: String(baseDoc?.dte_type_code || '33'),
        reference_folio: Number(baseDoc?.folio),
        reference_date: baseDoc?.date_issued, // Fecha de la factura que se anula
        reference_reason: 'Anula documento' // Razón genérica
      }
    ],
    details: [
      {
        item_name: `ANULA DOCUMENTO DE LA REFERENCIA - ${baseDoc?.dte_type_description || ''} N° ${baseDoc?.folio || ''
          }`,
        quantity: 1,
        unit_price: Number(baseUnitPrice ?? 0),
        discount_percent: 0,
        unit: 'UN'
      }
    ]
  }

  const printRef = useRef()

  const handleDownloadPdf = async () => {
    const element = printRef.current
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    })
    const data = canvas.toDataURL('image/png')

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: 'a4'
    })

    const imgProperties = pdf.getImageProperties(data)
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width

    pdf.addImage(data, 'PNG', 0, 0, pdfWidth, pdfHeight)
    pdf.save(`NotaCredito_${previewDocument?.folio || 'preview'}.pdf`)
  }

  if (loadingDetails) {
    return (
      <div className='bg-white p-10 rounded-2xl shadow flex justify-center items-center'>
        <p className='text-gray-500'>Cargando datos para vista previa...</p>
      </div>
    )
  }

  return (
    <div className='bg-white p-6 rounded-2xl shadow space-y-4'>
      <div className='flex items-start justify-between'>
        <div>
          <h2 className='text-2xl font-semibold'>REVISIÓN DE DOCUMENTO TRIBUTARIO ELECTRÓNICO</h2>
          <p className='text-sm text-gray-600 mt-1'>
            Este es el Documento Tributario Electrónico de Anulación que se ha generado con la
            información que Ud. ha seleccionado. Revíselo cuidadosamente para verificar que
            efectivamente se está realizando la Anulación que Ud. desea.
          </p>
        </div>

        <div className='text-sm w-1/3 text-gray-700'>
          <span className='mr-2'>Empresa:</span>
          <span className='font-semibold'>{currentBusiness?.rut || '-'}</span>
        </div>
      </div>

      <div className='border rounded overflow-auto bg-gray-100' style={{ height: '75vh' }}>
        <NotaCreditoPreviewPDF
          document={previewDocument}
          currentBusiness={currentBusiness}
          printRef={printRef}
        />
      </div>
      <section className='flex justify-center'>
        {' '}
        <button
          onClick={handleDownloadPdf}
          className='btn-detalle'
          style={{
            padding: '10px 20px',
            backgroundColor: '#000033',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Descargar PDF
        </button>
      </section>

      <p className='text-sm text-gray-700'>
        Si está de acuerdo con el documento desplegado y quiere emitir este documento, presione el
        botón <span className='font-semibold'>Firmar</span>.
        <br />
        <br />
        Si no está de acuerdo y quiere cancelar la generación del documento de Anulación, presione
        el botón <span className='font-semibold'>Volvera al menú principal</span>.
      </p>

      <div className='flex justify-center items-center gap-2'>
        <button type='button' onClick={() => onConfirm(previewDocument)} className='cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95'>
          Firmar
        </button>
        <button type='button' onClick={onBack} className='cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-primary/90 active:scale-95'>
          Volver al menú principal
        </button>
      </div>
    </div>
  )
}

